import React from 'react';
import { Group, Rect } from 'react-konva';
import type { Node } from '../../../core/models/scene';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';

interface ManifestKonvaNodeProps {
  node: Node;
  manifest: ShapeManifest<any>;
  selected: boolean;
  dimmed?: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
}

/**
 * Render a manifest that supplies its own Konva component via
 * `renderers.konvaNative`. Wraps the shape in the standard positioned
 * Group with drag/click/selection affordances so manifest authors only
 * need to render their shape body — not reimplement hit-testing.
 *
 * When a manifest only supplies `renderers.svg` (or neither), the
 * generic `ManifestSvgNode` card is used instead. This component is
 * the escape hatch for shapes whose visual is fundamentally different
 * from the icon-tile + label card pattern (e.g. compass_bubble's
 * multi-region account card with tier chips).
 */
export const ManifestKonvaNode: React.FC<ManifestKonvaNodeProps> = ({
  node,
  manifest,
  selected,
  dimmed,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
}) => {
  const width = (node.w ?? manifest.defaultDimensions?.width ?? 160) as number;
  const height = (node.h ?? manifest.defaultDimensions?.height ?? 60) as number;

  const manifestDefaults: Record<string, unknown> =
    typeof manifest.defaultProps === 'function'
      ? (manifest.defaultProps as any)()
      : (manifest.defaultProps as any) ?? {};
  const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
  const props = { ...manifestDefaults, ...attrs };

  const body = manifest.renderers.konvaNative?.({
    viewModel: { width, height, id: node.id, name: (node as any).name ?? node.id },
    props,
    handlers: {},
  });

  return (
    <Group
      x={node.x ?? 0}
      y={node.y ?? 0}
      opacity={dimmed ? 0.25 : 1}
      draggable={!!onDragStart}
      onClick={onClick}
      onTap={onClick}
      onDblClick={onDoubleClick}
      onDblTap={onDoubleClick}
      onDragStart={(e) => {
        const t = e.target;
        onDragStart?.({ x: t.x(), y: t.y() });
      }}
      onDragMove={(e) => {
        const t = e.target;
        onDragMove?.({ x: t.x(), y: t.y() });
      }}
      onDragEnd={(e) => {
        const t = e.target;
        onDragEnd?.({ x: t.x(), y: t.y() });
      }}
    >
      {body}
      {selected && (
        <Rect
          x={-2}
          y={-2}
          width={width + 4}
          height={height + 4}
          stroke="#3b82f6"
          strokeWidth={2}
          cornerRadius={6}
          listening={false}
        />
      )}
    </Group>
  );
};
